/**
 * «Enlace para tu cliente» (idea 9): cliente de la API.
 *  - Gestión del dueño (con sesión): `crearEnlace`, `listarEnlaces`, `revocarEnlace`, `actualizarEnlace`.
 *  - Lectura pública (SIN sesión, con el token en `X-Enlace-Token`): `leerEnlacePublico`, `bajarPlanoPublico`.
 *    Se usa `fetch` directo: no debe mandar cookie, ni tocar el estado de sesión, ni disparar los avisos
 *    globales de `apiFetch`.
 */
import type { AvisosDelEnlace, EnlacePublicoRespuesta, EnlaceResumen } from '@supernova/shared';
import { apiFetch } from './client.js';

export interface CrearEnlaceRespuesta {
  enlace: EnlaceResumen;
  /** El token con el que se arma el enlace (también se puede volver a pedir con `tokenDelEnlace`). */
  token: string;
  /** Lo que quien comparte debe saber antes de mandarlo (conceptos sin precio, por revisar). */
  avisos: AvisosDelEnlace;
}

export function crearEnlace(
  projectId: string,
  fileId: string,
  body: {
    expiraEnDias: number;
    mostrarPrecios: boolean;
    nombre?: string;
    mostrarAvance?: boolean;
    /** Solo staff: de qué fuente se congela el avance; ausente = la más al día. */
    avanceFuente?: { origen: 'm1' | 'm2'; obraRef: string };
  },
): Promise<CrearEnlaceRespuesta> {
  return apiFetch(`/api/v1/projects/${projectId}/planos/${fileId}/enlaces`, { method: 'POST', body });
}

/** Vuelve a pedir el token de un enlace vigente (para copiarlo o mandarlo otra vez). */
export async function tokenDelEnlace(projectId: string, enlaceId: string): Promise<string> {
  const r = await apiFetch<{ token: string }>(`/api/v1/projects/${projectId}/enlaces/${enlaceId}/token`);
  return r.token;
}

export async function listarEnlaces(projectId: string): Promise<EnlaceResumen[]> {
  const r = await apiFetch<{ enlaces: EnlaceResumen[] }>(`/api/v1/projects/${projectId}/enlaces`);
  return r.enlaces;
}

export async function revocarEnlace(projectId: string, enlaceId: string): Promise<EnlaceResumen> {
  const r = await apiFetch<{ enlace: EnlaceResumen }>(
    `/api/v1/projects/${projectId}/enlaces/${enlaceId}/revocar`,
    { method: 'POST' },
  );
  return r.enlace;
}

export async function actualizarEnlace(
  projectId: string,
  enlaceId: string,
  body: {
    mostrarPrecios?: boolean;
    mostrarAvance?: boolean;
    /** null = volver a «la más al día». */
    avanceFuente?: { origen: 'm1' | 'm2'; obraRef: string } | null;
  } = {},
): Promise<{ enlace: EnlaceResumen; avisos: AvisosDelEnlace }> {
  return apiFetch<{ enlace: EnlaceResumen; avisos: AvisosDelEnlace }>(
    `/api/v1/projects/${projectId}/enlaces/${enlaceId}/actualizar`,
    { method: 'POST', body },
  );
}

/** La URL que se le manda al cliente. El token va en el fragmento: no llega al servidor ni a los logs. */
export function urlDelEnlace(token: string, origen: string = window.location.origin): string {
  return `${origen}/compartido#t=${token}`;
}

/** El token del fragmento de la URL actual (`#t=…`), o null. */
export function tokenDeLaUrl(hash: string = window.location.hash): string | null {
  const m = /(?:^#|&)t=([A-Za-z0-9_-]{43})(?:&|$)/.exec(hash);
  return m ? m[1]! : null;
}

export type ErrorPublico = 'NO_DISPONIBLE' | 'DEMASIADAS' | 'SERVIDOR';

const MENSAJE_PUBLICO: Record<ErrorPublico, string> = {
  NO_DISPONIBLE: 'Este enlace ya no está disponible. Pídele uno nuevo a quien te lo envió.',
  DEMASIADAS: 'Demasiadas consultas seguidas. Espera un minuto y vuelve a abrir el enlace.',
  SERVIDOR: 'No se pudo cargar. Vuelve a intentar en unos segundos.',
};

/** Error de la lectura pública. `message` ya es texto para el cliente (el visor lo muestra tal cual). */
export class EnlacePublicoError extends Error {
  constructor(readonly tipo: ErrorPublico) {
    super(MENSAJE_PUBLICO[tipo]);
  }
}

async function getPublico(path: string, token: string, signal?: AbortSignal): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(path, {
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      signal,
      headers: { 'X-Enlace-Token': token },
    });
  } catch {
    throw new EnlacePublicoError('SERVIDOR');
  }
  if (res.ok) return res;
  if (res.status === 404) throw new EnlacePublicoError('NO_DISPONIBLE');
  if (res.status === 429) throw new EnlacePublicoError('DEMASIADAS');
  throw new EnlacePublicoError('SERVIDOR');
}

export async function leerEnlacePublico(token: string): Promise<EnlacePublicoRespuesta> {
  return (await getPublico('/api/v1/publico/enlace', token)).json() as Promise<EnlacePublicoRespuesta>;
}

export async function bajarPlanoPublico(token: string, signal?: AbortSignal): Promise<ArrayBuffer> {
  return (await getPublico('/api/v1/publico/enlace/plano', token, signal)).arrayBuffer();
}
